const MAX_WIDTH = 1920;
const MAX_SIZE_BYTES = 1 * 1024 * 1024; // 1MB

export interface CompressionResult {
  blob: Blob;
  wasCompressed: boolean;
}

export async function compressImage(file: File): Promise<CompressionResult> {
  // If not an image, return as-is
  if (!file.type.startsWith("image/")) {
    return { blob: file, wasCompressed: false };
  }

  // Skip compression for GIFs and SVGs
  if (file.type === "image/gif" || file.type === "image/svg+xml") {
    return { blob: file, wasCompressed: false };
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = async () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;
      let needsResize = false;

      // Check if we need to resize
      if (width > MAX_WIDTH) {
        height = Math.round((height * MAX_WIDTH) / width);
        width = MAX_WIDTH;
        needsResize = true;
      }

      // If file is small enough and doesn't need resize, return original
      if (!needsResize && file.size <= MAX_SIZE_BYTES) {
        resolve({ blob: file, wasCompressed: false });
        return;
      }

      // Create canvas and draw image
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Try different quality levels to get under 1MB
      let quality = 0.9;
      let blob: Blob | null = null;

      while (quality >= 0.1) {
        blob = await new Promise<Blob | null>((res) => {
          canvas.toBlob(
            (b) => res(b),
            "image/jpeg",
            quality
          );
        });

        if (blob && blob.size <= MAX_SIZE_BYTES) {
          break;
        }

        quality -= 0.1;
      }

      if (!blob) {
        reject(new Error("Failed to compress image"));
        return;
      }

      resolve({ blob, wasCompressed: true });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
}
